import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Button, Card, List, Modal, Spin, Alert, App, Input,
  Select, Row, Col, Tag, Space, Empty, Switch, AutoComplete,
} from 'antd';
import {
  PlusOutlined, ShopOutlined, EditOutlined, InboxOutlined, RightOutlined, UndoOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getLocaisCompra, arquivarLocalCompra,
  reativarLocalCompra, desmembrarLocal, getListasCompra,
} from '../../services/comprasService';
import { getCategorias } from '../../services/financeService';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';
import { getErrorMessage } from '../../utils/errorHandler';
import LocalCompraModal, { nomeEhGenerico } from '../../components/LocalCompraModal/LocalCompraModal';
import NovaListaModal from '../../components/NovaListaModal/NovaListaModal';

const { Content } = Layout;
const { Title, Text } = Typography;

const brl = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const fmtData = (d) => (d ? dayjs(String(d).slice(0, 10)).format('DD/MM/YYYY') : '—');

const STATUS_TAG = {
  RASCUNHO: { color: 'default', label: 'Rascunho' },
  EM_COMPRA: { color: 'processing', label: 'Em compra' },
  CONCLUIDA: { color: 'gold', label: 'Concluída' },
  LANCADA: { color: 'green', label: 'Lançada' },
  CANCELADA: { color: 'red', label: 'Cancelada' },
};

function ListasComprasPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();

  const [locais, setLocais] = useState([]);
  const [locaisArquivados, setLocaisArquivados] = useState([]);
  const [listas, setListas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroLocal, setFiltroLocal] = useState('todos');
  const [mostrarArquivados, setMostrarArquivados] = useState(false);
  const [mostrarFeitas, setMostrarFeitas] = useState(false);
  const [busca, setBusca] = useState('');

  const [localModalOpen, setLocalModalOpen] = useState(false);
  const [localEmEdicao, setLocalEmEdicao] = useState(null);
  const [listaModalOpen, setListaModalOpen] = useState(false);
  const [organizarLocal, setOrganizarLocal] = useState(null);
  const [modal, modalCtx] = Modal.useModal();

  const locaisGenericos = useMemo(
    () => locais.filter((l) => l.eh_generico ?? nomeEhGenerico(l.nome)),
    [locais],
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [loc, arq, lst] = await Promise.all([
        getLocaisCompra(),
        getLocaisCompra({ status: 'ARQUIVADO' }),
        getListasCompra(),
      ]);
      setLocais(loc);
      setLocaisArquivados(arq);
      setListas(lst);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao carregar listas de compras.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    getCategorias().then((d) => setCategorias(Array.isArray(d) ? d : d?.results || [])).catch(() => {});
  }, [fetchData]);

  // --- Local ---
  const abrirCriarLocal = () => {
    setLocalEmEdicao(null);
    setLocalModalOpen(true);
  };
  const abrirEditarLocal = (local) => {
    setLocalEmEdicao(local);
    setLocalModalOpen(true);
  };

  const arquivarLocal = (local) => {
    modal.confirm({
      title: `Arquivar "${local.nome}"?`,
      content: 'As listas do local continuam acessíveis. Você pode reativá-lo depois em “Exibir arquivados”.',
      okText: 'Arquivar',
      okType: 'danger',
      onOk: async () => {
        try {
          await arquivarLocalCompra(local.id);
          message.success('Local arquivado.');
          fetchData();
        } catch (err) {
          message.error(getErrorMessage(err, 'Erro ao arquivar.'));
        }
      },
    });
  };

  const reativarLocal = async (local) => {
    try {
      await reativarLocalCompra(local.id);
      message.success('Local reativado.');
      fetchData();
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao reativar.'));
    }
  };

  // --- Lista ---
  const abrirNovaLista = () => {
    setListaModalOpen(true);
  };

  const ABERTAS_STATUS = ['RASCUNHO', 'EM_COMPRA'];
  const listasVisiveis = useMemo(() => {
    let base = listas.filter((l) => !l.eh_modelo);
    if (filtroLocal !== 'todos') base = base.filter((l) => l.local === Number(filtroLocal));
    const termo = busca.trim().toLowerCase();
    if (termo) {
      base = base.filter(
        (l) =>
          (l.titulo || '').toLowerCase().includes(termo) ||
          (l.local_nome || '').toLowerCase().includes(termo),
      );
    }
    return {
      abertas: base.filter((l) => ABERTAS_STATUS.includes(l.status)),
      feitas: base.filter((l) => !ABERTAS_STATUS.includes(l.status)),
    };
  }, [listas, filtroLocal, busca]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
        <Spin size="large" tip="Carregando..." />
      </div>
    );
  }

  return (
    <Content style={{ padding: 'clamp(1rem, 4vw, 2rem)', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {modalCtx}
      <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
        <Col xs={24} md={14}>
          <Title level={2} style={{ margin: 0 }}>Listas de compras</Title>
          <Text type="secondary">
            Monte a lista, acompanhe o total em tempo real e lance a compra no financeiro.
          </Text>
        </Col>
        <Col xs={24} md={10} style={{ textAlign: 'right' }}>
          <Space wrap>
            {locaisArquivados.length > 0 && (
              <Space size={6}>
                <Text type="secondary" style={{ fontSize: 12 }}>Arquivados</Text>
                <Switch size="small" checked={mostrarArquivados} onChange={setMostrarArquivados} />
              </Space>
            )}
            <Button icon={<ShopOutlined />} onClick={abrirCriarLocal}>Novo local</Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={abrirNovaLista}
              disabled={locais.length === 0}
            >
              Nova lista
            </Button>
          </Space>
        </Col>
      </Row>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {locaisGenericos.map((l) => (
        <Alert
          key={l.id}
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`"${l.nome}" parece um tipo de loja, não um estabelecimento`}
          description="O comparador de preços precisa de lojas específicas (ex.: Assaí Aterrado, Extra Tijuca)."
          action={
            <Button size="small" onClick={() => setOrganizarLocal(l)}>Organizar</Button>
          }
        />
      ))}

      {locais.length === 0 && locaisArquivados.length === 0 ? (
        <Card>
          <EmptyStateGuide
            title="Nenhum estabelecimento cadastrado"
            description="Crie um local (supermercado, ferragista...) e comece a montar suas listas de compras."
            actionLabel="Novo local"
            onAction={abrirCriarLocal}
          />
        </Card>
      ) : (
        <>
          {locais.length > 0 && (
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {locais.map((local) => {
              const abertas = listas.filter(
                (l) => l.local === local.id && !l.eh_modelo && ['RASCUNHO', 'EM_COMPRA'].includes(l.status),
              ).length;
              const temAbertas = abertas > 0;
              return (
                <Col xs={24} sm={12} lg={8} key={local.id}>
                  <Card
                    size="small"
                    hoverable
                    onClick={() => navigate(`/listas-compras/locais/${local.id}`)}
                    style={{ borderLeft: `4px solid ${temAbertas ? '#d4a017' : '#d9d9d9'}` }}
                    actions={[
                      <EditOutlined key="edit" onClick={(e) => { e.stopPropagation(); abrirEditarLocal(local); }} />,
                      <InboxOutlined key="arch" onClick={(e) => { e.stopPropagation(); arquivarLocal(local); }} />,
                    ]}
                  >
                    <Card.Meta
                      avatar={<ShopOutlined style={{ fontSize: 22, color: '#50B0A0' }} />}
                      title={local.nome}
                      description={
                        <Space direction="vertical" size={6}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {local.categoria_nome || 'Sem categoria padrão'}
                          </Text>
                          <Tag color={temAbertas ? 'gold' : 'default'} style={{ marginRight: 0 }}>
                            {temAbertas ? `${abertas} lista(s) em aberto` : 'Nenhuma lista em aberto'}
                          </Tag>
                        </Space>
                      }
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>
          )}

          {mostrarArquivados && locaisArquivados.length > 0 && (
            <Card size="small" title="Locais arquivados" style={{ marginBottom: 24 }} styles={{ body: { padding: 0 } }}>
              <List
                dataSource={locaisArquivados}
                renderItem={(local) => (
                  <List.Item
                    style={{ padding: '10px 16px' }}
                    actions={[
                      <Button
                        key="r"
                        type="link"
                        icon={<UndoOutlined />}
                        onClick={() => reativarLocal(local)}
                      >
                        Reativar
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<ShopOutlined style={{ color: '#999' }} />}
                      title={<Text type="secondary">{local.nome}</Text>}
                      description={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {local.categoria_nome || 'Sem categoria padrão'}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          )}

          <Card
            title="Todas as listas"
            styles={{ body: { padding: 0 } }}
          >
            <div
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #f0f0f0',
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <Input
                allowClear
                prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                placeholder="Buscar lista"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                style={{ flex: '1 1 180px', minWidth: 0 }}
              />
              <Select
                value={filtroLocal}
                onChange={setFiltroLocal}
                showSearch
                optionFilterProp="label"
                style={{ flex: '0 1 220px', minWidth: 150 }}
                options={[
                  { label: 'Todos os locais', value: 'todos' },
                  ...locais.map((l) => ({ label: l.nome, value: String(l.id) })),
                ]}
              />
            </div>
            {(() => {
              const renderItem = (lista) => {
                const st = STATUS_TAG[lista.status] || STATUS_TAG.RASCUNHO;
                const totalMostrar = Number(lista.total_final) > 0 ? lista.total_final : lista.total_estimado;
                return (
                  <List.Item
                    style={{ padding: '14px 20px', cursor: 'pointer' }}
                    onClick={() => navigate(`/listas-compras/${lista.id}`)}
                    actions={[<RightOutlined key="go" style={{ color: '#bbb' }} />]}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          {lista.titulo || 'Lista'}
                          <Tag color={st.color}>{st.label}</Tag>
                        </Space>
                      }
                      description={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {lista.local_nome || 'Sem local'} · {fmtData(lista.data_compra)} · {lista.total_itens} item(ns)
                        </Text>
                      }
                    />
                    <Text strong>{brl(totalMostrar)}</Text>
                  </List.Item>
                );
              };
              const secHeader = { padding: '8px 20px', fontSize: 12, fontWeight: 600, color: '#8c8c8c', background: '#fafafa', borderBottom: '1px solid #f0f0f0' };
              return (
                <>
                  <div style={secHeader}>Em aberto ({listasVisiveis.abertas.length})</div>
                  {listasVisiveis.abertas.length > 0 ? (
                    <List dataSource={listasVisiveis.abertas} renderItem={renderItem} />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={busca.trim() ? 'Nenhuma lista em aberto para esta busca' : 'Nenhuma lista em aberto'}
                      style={{ padding: '24px 0' }}
                    >
                      {!busca.trim() && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={abrirNovaLista}>
                          Nova lista
                        </Button>
                      )}
                    </Empty>
                  )}

                  {listasVisiveis.feitas.length > 0 && (
                    <>
                      <div
                        style={{ ...secHeader, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                        onClick={() => setMostrarFeitas((v) => !v)}
                      >
                        <RightOutlined style={{ fontSize: 10, transform: mostrarFeitas ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
                        Concluídas e lançadas ({listasVisiveis.feitas.length})
                      </div>
                      {mostrarFeitas && <List dataSource={listasVisiveis.feitas} renderItem={renderItem} />}
                    </>
                  )}
                </>
              );
            })()}
          </Card>
        </>
      )}

      <LocalCompraModal
        open={localModalOpen}
        local={localEmEdicao}
        categorias={categorias}
        onClose={() => setLocalModalOpen(false)}
        onSaved={() => { setLocalModalOpen(false); fetchData(); }}
      />

      <NovaListaModal
        open={listaModalOpen}
        locais={locais}
        localPadrao={filtroLocal !== 'todos' ? Number(filtroLocal) : undefined}
        onClose={() => setListaModalOpen(false)}
        onCreated={(nova) => { setListaModalOpen(false); navigate(`/listas-compras/${nova.id}`); }}
      />

      <OrganizarLocalModal
        local={organizarLocal}
        onClose={() => setOrganizarLocal(null)}
        onDone={() => { setOrganizarLocal(null); fetchData(); }}
      />
    </Content>
  );
}

// "Organizar": redistribui as listas de um local genérico em estabelecimentos específicos
function OrganizarLocalModal({ local, onClose, onDone }) {
  const { message } = App.useApp();
  const [listasLocal, setListasLocal] = useState([]);
  const [nomes, setNomes] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!local) return;
    setLoading(true);
    setNomes({});
    getListasCompra({ local: local.id })
      .then((rows) => setListasLocal(rows.filter((r) => !r.eh_modelo)))
      .catch(() => setListasLocal([]))
      .finally(() => setLoading(false));
  }, [local]);

  const salvar = async () => {
    const atribuicoes = listasLocal
      .filter((l) => (nomes[l.id] || '').trim())
      .map((l) => ({ lista_id: l.id, estabelecimento: nomes[l.id].trim() }));
    if (atribuicoes.length === 0) {
      message.warning('Informe pelo menos um estabelecimento.');
      return;
    }
    setSaving(true);
    try {
      await desmembrarLocal(local.id, atribuicoes);
      message.success('Listas reorganizadas.');
      onDone();
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao organizar.'));
    } finally {
      setSaving(false);
    }
  };

  const sugestoes = [...new Set(listasLocal.map((l) => l.local_nome).filter(Boolean))];

  return (
    <Modal
      title={local ? `Organizar "${local.nome}"` : 'Organizar'}
      open={!!local}
      onCancel={onClose}
      onOk={salvar}
      confirmLoading={saving}
      okText="Salvar"
      width={600}
      destroyOnClose
    >
      <Text type="secondary">
        Para cada lista, informe em qual loja específica a compra foi feita. As listas (e o
        histórico de preço) passam para o novo estabelecimento.
      </Text>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      ) : (
        <List
          style={{ marginTop: 12 }}
          dataSource={listasLocal}
          locale={{ emptyText: 'Nenhuma lista neste local.' }}
          renderItem={(l) => (
            <List.Item>
              <div style={{ flex: 1 }}>
                <Text>{l.titulo || 'Lista'}</Text>
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                  {fmtData(l.data_compra)} · {STATUS_TAG[l.status]?.label}
                </Text>
              </div>
              <AutoComplete
                style={{ width: 220 }}
                placeholder="Nome da loja"
                value={nomes[l.id] || ''}
                onChange={(v) => setNomes((n) => ({ ...n, [l.id]: v }))}
                options={sugestoes.map((s) => ({ value: s }))}
              />
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
}

export default ListasComprasPage;
